import { useRef, useState, useCallback } from "react";
import { NodeViewWrapper, NodeViewProps } from "@tiptap/react";
import { cn } from "../../lib/utils";

const MIN_WIDTH = 50;
const MIN_HEIGHT = 50;

export const ImageNode = ({ node, updateAttributes, selected }: NodeViewProps) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<string | null>(null);
  const startPos = useRef({ x: 0, y: 0, width: 0, height: 0 });

  const { src, alt, width, height } = node.attrs;

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, direction: string) => {
      e.preventDefault();
      e.stopPropagation();

      const img = imageRef.current;
      if (!img) return;

      setIsResizing(true);
      setResizeDirection(direction);
      startPos.current = {
        x: e.clientX,
        y: e.clientY,
        width: img.offsetWidth,
        height: img.offsetHeight,
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - startPos.current.x;
        const deltaY = moveEvent.clientY - startPos.current.y;

        let newWidth = startPos.current.width;
        let newHeight = startPos.current.height;
        const aspectRatio = startPos.current.width / startPos.current.height;

        if (direction.includes("e")) {
          newWidth = Math.max(MIN_WIDTH, startPos.current.width + deltaX);
        }
        if (direction.includes("w")) {
          newWidth = Math.max(MIN_WIDTH, startPos.current.width - deltaX);
        }
        if (direction.includes("s")) {
          newHeight = Math.max(MIN_HEIGHT, startPos.current.height + deltaY);
        }
        if (direction.includes("n")) {
          newHeight = Math.max(MIN_HEIGHT, startPos.current.height - deltaY);
        }

        // Maintain aspect ratio for corner handles
        if (direction.length === 2) {
          if (direction.includes("e") || direction.includes("w")) {
            newHeight = newWidth / aspectRatio;
          } else {
            newWidth = newHeight * aspectRatio;
          }
        }

        updateAttributes({ width: Math.round(newWidth), height: Math.round(newHeight) });
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        setResizeDirection(null);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [updateAttributes]
  );

  const handleImageLoad = useCallback(() => {
    const img = imageRef.current;
    if (img && !width && !height) {
      // Set initial dimensions based on natural size, capped at container width
      const maxWidth = img.parentElement?.clientWidth || 600;
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;

      if (naturalWidth > maxWidth) {
        const ratio = maxWidth / naturalWidth;
        updateAttributes({
          width: Math.round(maxWidth),
          height: Math.round(naturalHeight * ratio),
        });
      } else {
        updateAttributes({
          width: naturalWidth,
          height: naturalHeight,
        });
      }
    }
  }, [width, height, updateAttributes]);

  return (
    <NodeViewWrapper className="relative inline-block my-4">
      <div
        className={cn(
          "relative inline-block",
          selected && "ring-2 ring-primary ring-offset-2 rounded-md",
          isResizing && "select-none"
        )}
      >
        <img
          ref={imageRef}
          src={src}
          alt={alt || ""}
          width={width || undefined}
          height={height || undefined}
          onLoad={handleImageLoad}
          className="block rounded-md"
          draggable={false}
        />

        {/* Alt text tooltip */}
        {alt && selected && (
          <div className="absolute bottom-full left-0 mb-1 px-2 py-1 bg-black/75 text-white text-xs rounded whitespace-nowrap max-w-xs truncate">
            {alt}
          </div>
        )}

        {/* Resize handles - only show when selected */}
        {selected && (
          <>
            {/* Corner handles */}
            <div
              className={cn(
                "absolute -top-1 -left-1 w-3 h-3 bg-primary border border-white rounded-sm cursor-nw-resize",
                resizeDirection === "nw" && "bg-primary/80"
              )}
              onMouseDown={(e) => handleMouseDown(e, "nw")}
            />
            <div
              className={cn(
                "absolute -top-1 -right-1 w-3 h-3 bg-primary border border-white rounded-sm cursor-ne-resize",
                resizeDirection === "ne" && "bg-primary/80"
              )}
              onMouseDown={(e) => handleMouseDown(e, "ne")}
            />
            <div
              className={cn(
                "absolute -bottom-1 -left-1 w-3 h-3 bg-primary border border-white rounded-sm cursor-sw-resize",
                resizeDirection === "sw" && "bg-primary/80"
              )}
              onMouseDown={(e) => handleMouseDown(e, "sw")}
            />
            <div
              className={cn(
                "absolute -bottom-1 -right-1 w-3 h-3 bg-primary border border-white rounded-sm cursor-se-resize",
                resizeDirection === "se" && "bg-primary/80"
              )}
              onMouseDown={(e) => handleMouseDown(e, "se")}
            />

            {/* Edge handles */}
            <div
              className={cn(
                "absolute top-1/2 -left-1 w-2 h-6 -translate-y-1/2 bg-primary border border-white rounded-sm cursor-w-resize",
                resizeDirection === "w" && "bg-primary/80"
              )}
              onMouseDown={(e) => handleMouseDown(e, "w")}
            />
            <div
              className={cn(
                "absolute top-1/2 -right-1 w-2 h-6 -translate-y-1/2 bg-primary border border-white rounded-sm cursor-e-resize",
                resizeDirection === "e" && "bg-primary/80"
              )}
              onMouseDown={(e) => handleMouseDown(e, "e")}
            />
            <div
              className={cn(
                "absolute -top-1 left-1/2 w-6 h-2 -translate-x-1/2 bg-primary border border-white rounded-sm cursor-n-resize",
                resizeDirection === "n" && "bg-primary/80"
              )}
              onMouseDown={(e) => handleMouseDown(e, "n")}
            />
            <div
              className={cn(
                "absolute -bottom-1 left-1/2 w-6 h-2 -translate-x-1/2 bg-primary border border-white rounded-sm cursor-s-resize",
                resizeDirection === "s" && "bg-primary/80"
              )}
              onMouseDown={(e) => handleMouseDown(e, "s")}
            />
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
};
